import { inject, Injectable, InjectionToken } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

export interface PlatformSession {
  accessToken: string;
  expiresAt: number;
  user: { id: string; displayName: string; roles: string[]; identityReference: string; linked: boolean };
}

export const IDENTITY_NAVIGATION = new InjectionToken<{assign(url: string): void}>('identityNavigation', {
  providedIn: 'root', factory: () => ({ assign: url => window.location.assign(url) }),
});

export const IDENTITY_REFRESH_LOCK = new InjectionToken<{run<T>(work: () => Promise<T>): Promise<T>}>('identityRefreshLock', {
  providedIn: 'root', factory: () => ({
    async run<T>(work: () => Promise<T>): Promise<T> {
      return typeof navigator !== 'undefined' && navigator.locks
        ? await navigator.locks.request('shine-identity-refresh', { signal: AbortSignal.timeout(10_000) }, work)
        : work();
    },
  }),
});

@Injectable({ providedIn: 'root' })
export class Auth0SessionService {
  // Session requests bypass bearer interceptors to avoid recursion. Refresh stays HttpOnly.
  private readonly http = new HttpClient(inject(HttpBackend));
  private readonly navigation = inject(IDENTITY_NAVIGATION);
  private readonly refreshLock = inject(IDENTITY_REFRESH_LOCK);
  private readonly base = new URL('api/auth/', environment.authBaseServerUrl).href;
  private readonly state = new BehaviorSubject<PlatformSession | null>(null);
  readonly session$ = this.state.asObservable();
  private pending: Promise<PlatformSession | null> | null = null;
  private generation = 0;
  private initialized = false;
  private signingOut = false;

  get session(): PlatformSession | null { return this.state.value; }

  async ensureSession(forceRefresh = false): Promise<PlatformSession | null> {
    if (this.signingOut) return null;
    if (!forceRefresh && this.session && this.session.expiresAt > Date.now() + 30_000) return this.session;
    if (!forceRefresh && this.initialized && !this.session) return null;
    if (this.pending) return this.pending;
    const generation = this.generation;
    this.pending = this.load(forceRefresh).then(session => {
      if (generation !== this.generation) return null;
      this.initialized = true;
      this.state.next(session);
      return session;
    }).finally(() => { this.pending = null; });
    return this.pending;
  }

  private async load(refresh: boolean): Promise<PlatformSession | null> {
    try {
      const response = refresh
        ? await this.refreshLock.run(() => this.refreshAfterCookieCheck())
        : await firstValueFrom(this.http.get<PlatformSession>(`${this.base}identity/session`, { withCredentials: true }).pipe(timeout(10_000)));
      if (!response || typeof response.accessToken !== 'string' || response.accessToken.length > 32768 ||
          !Number.isFinite(response.expiresAt) || response.expiresAt <= Date.now() ||
          !response.user || typeof response.user.id !== 'string' ||
          typeof response.user.displayName !== 'string' || !Array.isArray(response.user.roles) ||
          response.user.roles.some(role => typeof role !== 'string') ||
          !/^[a-f0-9]{64}$/.test(response.user.identityReference) || typeof response.user.linked !== 'boolean') {
        throw new Error('Invalid Shine session response');
      }
      return response;
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 401) return refresh ? null : this.load(true);
      throw error;
    }
  }

  private async refreshAfterCookieCheck(): Promise<PlatformSession> {
    // Another Backoffice tab may have rotated the shared cookies while this tab waited.
    try {
      const latest = await firstValueFrom(this.http.get<PlatformSession>(`${this.base}identity/session`, { withCredentials: true }).pipe(timeout(10_000)));
      if (latest.accessToken !== this.session?.accessToken && latest.expiresAt > Date.now() + 30_000) return latest;
    } catch (error) {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) throw error;
    }
    return firstValueFrom(this.http.post<PlatformSession>(`${this.base}identity/refresh`, {}, { withCredentials: true }).pipe(timeout(10_000)));
  }

  login(returnPath = '/', mode?: 'staff'): void {
    const path = returnPath.startsWith('/') && !returnPath.startsWith('//') && !/[\\\r\n]/.test(returnPath) ? returnPath : '/';
    const query = new URLSearchParams({ returnPath: path });
    if (mode === 'staff') query.set('mode', 'staff');
    this.navigation.assign(`${this.base}auth0/login?${query}`);
  }

  async logout(): Promise<void> {
    this.signingOut = true;
    this.generation++;
    this.initialized = true;
    this.state.next(null);
    // Await revocation before claiming logout completed or leaving the application.
    const result = await firstValueFrom(this.http.post<{ logoutUrl: string }>(`${this.base}identity/logout`, {}, { withCredentials: true }).pipe(timeout(10_000)));
    const url = new URL(result.logoutUrl);
    const issuer = new URL(environment.auth0Issuer);
    if (url.origin !== issuer.origin || url.username || url.password || url.pathname !== '/v2/logout') {
      throw new Error('Invalid sign-out URL');
    }
    this.navigation.assign(url.href);
  }
}
